import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CustomThemeService } from './custom-theme.service';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * GA4 Enhanced Ecommerce Item structure
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart_item
 */
export interface GA4Item {
  item_id: string; // Product SKU or ID
  item_name: string; // Product name
  affiliation?: string; // Store or affiliate name
  coupon?: string; // Item-level coupon code
  discount?: number; // Item discount amount
  index?: number; // Position in list
  item_brand?: string; // Product brand
  item_category?: string; // Primary category
  item_category2?: string; // Secondary category
  item_category3?: string; // Tertiary category
  item_category4?: string; // Quaternary category
  item_category5?: string; // Quinary category
  item_list_id?: string; // List ID where item was shown
  item_list_name?: string; // List name where item was shown
  item_variant?: string; // Product variant (size, color, etc.)
  location_id?: string; // Store location ID
  price: number; // Price per unit (required for revenue calculation)
  quantity: number; // Quantity (required)
}

/**
 * Add to cart event parameters
 */
export interface AddToCartEvent {
  currency: string;
  value: number;
  items: GA4Item[];
}

/**
 * Remove from cart event parameters
 */
export interface RemoveFromCartEvent {
  currency: string;
  value: number;
  items: GA4Item[];
}

/**
 * View item event parameters
 */
export interface ViewItemEvent {
  currency: string;
  value: number;
  items: GA4Item[];
}

/**
 * View cart event parameters
 */
export interface ViewCartEvent {
  currency: string;
  value: number;
  items: GA4Item[];
}

/**
 * Begin checkout event parameters
 */
export interface BeginCheckoutEvent {
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
}

/**
 * Add shipping info event parameters
 */
export interface AddShippingInfoEvent {
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
  shipping_tier?: string;
}

/**
 * Add payment info event parameters
 */
export interface AddPaymentInfoEvent {
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
  payment_type?: string;
}

/**
 * Purchase event parameters
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
 */
export interface PurchaseEvent {
  transaction_id: string; // Unique transaction identifier
  currency: string; // ISO 4217 currency code
  value: number; // Total revenue (including tax, shipping, discounts)
  items: GA4Item[]; // List of items purchased
  affiliation?: string; // Store or affiliate name
  coupon?: string; // Order-level coupon code
  shipping?: number; // Shipping cost
  tax?: number; // Tax amount
}

/**
 * View item list event parameters
 */
export interface ViewItemListEvent {
  item_list_id?: string;
  item_list_name?: string;
  items: GA4Item[];
}

/**
 * Select item event parameters
 */
export interface SelectItemEvent {
  item_list_id?: string;
  item_list_name?: string;
  items: GA4Item[];
}

/**
 * Promotion event parameters
 */
export interface PromotionEvent {
  creative_name?: string;
  creative_slot?: string;
  promotion_id?: string;
  promotion_name?: string;
  items?: GA4Item[];
}

/**
 * Refund event parameters
 */
export interface RefundEvent {
  transaction_id: string;
  currency: string;
  value: number;
  affiliation?: string;
  coupon?: string;
  shipping?: number;
  tax?: number;
  items?: GA4Item[];
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private router = inject(Router);
  private themeService = inject(CustomThemeService);
  private isInitialized = false;
  private debugMode = !environment.production;

  /**
   * Get the store affiliation name from the active manifest (per-store) and
   * fall back to the baked-in environment defaults.
   */
  private get affiliation(): string {
    const manifestName = this.themeService.getManifestRestaurant()?.name;
    return (
      manifestName ||
      environment.restaurant?.name ||
      'Happy Ordering'
    );
  }

  private get analyticsId(): string | null {
    return (
      this.themeService.getManifestGoogleAnalyticsId() ||
      environment.googleAnalyticsId ||
      null
    );
  }

  /**
   * Initialize Google Analytics
   * Loads the gtag.js script and sets up page view tracking
   */
  initialize(): void {
    const analyticsId = this.analyticsId;
    if (!analyticsId) {
      this.log('warn', 'Google Analytics ID not configured');
      return;
    }

    if (this.isInitialized) {
      this.log('warn', 'Google Analytics already initialized');
      return;
    }

    try {
      // Load GA script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
      document.head.appendChild(script);

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', analyticsId, {
        send_page_view: false, // We'll handle page views manually
        debug_mode: this.debugMode
      });

      this.isInitialized = true;

      // Track page views on route changes
      this.trackPageViews();

      this.log('info', `Google Analytics initialized with ID: ${analyticsId}`);
    } catch (error) {
      this.log('error', 'Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Track page views automatically on route changes
   */
  private trackPageViews(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.sendEvent('page_view', {
          page_path: event.urlAfterRedirects,
          page_title: document.title,
          page_location: window.location.href
        });
      });
  }

  /**
   * Helper to create a properly formatted GA4 item
   */
  createItem(params: {
    id: string | number;
    name: string;
    price: number;
    quantity: number;
    category?: string;
    category2?: string;
    category3?: string;
    variant?: string;
    brand?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    listId?: string;
    listName?: string;
  }): GA4Item {
    const item: GA4Item = {
      item_id: String(params.id),
      item_name: this.cleanText(params.name),
      price: this.ensureNumber(params.price),
      quantity: Math.max(1, Math.round(params.quantity)),
      affiliation: this.affiliation
    };

    // Add optional fields only if they have values
    if (params.category) item.item_category = params.category;
    if (params.category2) item.item_category2 = params.category2;
    if (params.category3) item.item_category3 = params.category3;
    if (params.variant) item.item_variant = params.variant;
    if (params.brand) item.item_brand = params.brand;
    if (params.coupon) item.coupon = params.coupon;
    if (params.discount !== undefined && params.discount > 0) {
      item.discount = this.ensureNumber(params.discount);
    }
    if (params.index !== undefined) item.index = params.index;
    if (params.listId) item.item_list_id = params.listId;
    if (params.listName) item.item_list_name = params.listName;

    return item;
  }

  /**
   * Track when a user views an item/product
   */
  trackViewItem(event: ViewItemEvent): void {
    const items = this.formatItems(event.items);
    this.sendEvent('view_item', {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    });
  }

  /**
   * Track when a user adds an item to cart
   */
  trackAddToCart(event: AddToCartEvent): void {
    const items = this.formatItems(event.items);
    this.sendEvent('add_to_cart', {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    });
  }

  /**
   * Track when user removes item from cart
   */
  trackRemoveFromCart(event: RemoveFromCartEvent): void {
    const items = this.formatItems(event.items);
    this.sendEvent('remove_from_cart', {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    });
  }

  /**
   * Track when user views their cart
   */
  trackViewCart(event: ViewCartEvent): void {
    const items = this.formatItems(event.items);
    this.sendEvent('view_cart', {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    });
  }

  /**
   * Track when a user begins checkout
   */
  trackBeginCheckout(event: BeginCheckoutEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    };

    if (event.coupon) eventData.coupon = event.coupon;

    this.sendEvent('begin_checkout', eventData);
  }

  /**
   * Track when user adds shipping information
   */
  trackAddShippingInfo(event: AddShippingInfoEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    };

    if (event.coupon) eventData.coupon = event.coupon;
    if (event.shipping_tier) eventData.shipping_tier = event.shipping_tier;

    this.sendEvent('add_shipping_info', eventData);
  }

  /**
   * Track when user adds payment information
   */
  trackAddPaymentInfo(event: AddPaymentInfoEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = {
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items
    };

    if (event.coupon) eventData.coupon = event.coupon;
    if (event.payment_type) eventData.payment_type = event.payment_type;

    this.sendEvent('add_payment_info', eventData);
  }

  /**
   * Track when a user completes a purchase
   * This is the most important event for e-commerce tracking
   */
  trackPurchase(event: PurchaseEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = {
      transaction_id: String(event.transaction_id),
      currency: event.currency,
      value: this.ensureNumber(event.value),
      items,
      affiliation: event.affiliation || this.affiliation
    };

    // Add optional fields
    if (event.coupon) eventData.coupon = event.coupon;
    if (event.shipping !== undefined) {
      eventData.shipping = this.ensureNumber(event.shipping);
    }
    if (event.tax !== undefined) {
      eventData.tax = this.ensureNumber(event.tax);
    }

    this.sendEvent('purchase', eventData);
  }

  /**
   * Track when user views an item list (category page, search results)
   */
  trackViewItemList(event: ViewItemListEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = { items };

    if (event.item_list_id) eventData.item_list_id = event.item_list_id;
    if (event.item_list_name) eventData.item_list_name = event.item_list_name;

    this.sendEvent('view_item_list', eventData);
  }

  /**
   * Track when user selects an item from a list
   */
  trackSelectItem(event: SelectItemEvent): void {
    const items = this.formatItems(event.items);
    const eventData: any = { items };

    if (event.item_list_id) eventData.item_list_id = event.item_list_id;
    if (event.item_list_name) eventData.item_list_name = event.item_list_name;

    this.sendEvent('select_item', eventData);
  }

  /**
   * Track when user views a promotion
   */
  trackViewPromotion(event: PromotionEvent): void {
    const eventData: any = {};

    if (event.creative_name) eventData.creative_name = event.creative_name;
    if (event.creative_slot) eventData.creative_slot = event.creative_slot;
    if (event.promotion_id) eventData.promotion_id = event.promotion_id;
    if (event.promotion_name) eventData.promotion_name = event.promotion_name;
    if (event.items) eventData.items = this.formatItems(event.items);

    this.sendEvent('view_promotion', eventData);
  }

  /**
   * Track when user selects a promotion
   */
  trackSelectPromotion(event: PromotionEvent): void {
    const eventData: any = {};

    if (event.creative_name) eventData.creative_name = event.creative_name;
    if (event.creative_slot) eventData.creative_slot = event.creative_slot;
    if (event.promotion_id) eventData.promotion_id = event.promotion_id;
    if (event.promotion_name) eventData.promotion_name = event.promotion_name;
    if (event.items) eventData.items = this.formatItems(event.items);

    this.sendEvent('select_promotion', eventData);
  }

  /**
   * Track refund
   */
  trackRefund(event: RefundEvent): void {
    const eventData: any = {
      transaction_id: String(event.transaction_id),
      currency: event.currency,
      value: this.ensureNumber(event.value)
    };

    if (event.affiliation) eventData.affiliation = event.affiliation;
    if (event.coupon) eventData.coupon = event.coupon;
    if (event.shipping !== undefined) {
      eventData.shipping = this.ensureNumber(event.shipping);
    }
    if (event.tax !== undefined) {
      eventData.tax = this.ensureNumber(event.tax);
    }
    if (event.items) eventData.items = this.formatItems(event.items);

    this.sendEvent('refund', eventData);
  }

  /**
   * Track a custom event
   * @param eventName - Name of the event
   * @param eventParams - Additional parameters for the event
   */
  trackEvent(eventName: string, eventParams?: Record<string, any>): void {
    this.sendEvent(eventName, eventParams);
  }

  /**
   * Set user ID for cross-device tracking
   */
  setUserId(userId: string | null): void {
    if (!this.canTrack()) return;

    try {
      if (userId) {
        window.gtag('set', { user_id: userId });
        this.log('info', `GA User ID set: ${userId}`);
      } else {
        window.gtag('set', { user_id: null });
        this.log('info', 'GA User ID cleared');
      }
    } catch (error) {
      this.log('error', 'Failed to set user ID:', error);
    }
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.canTrack()) return;

    try {
      window.gtag('set', 'user_properties', properties);
      this.log('info', 'GA User Properties set:', properties);
    } catch (error) {
      this.log('error', 'Failed to set user properties:', error);
    }
  }

  // ============== Private Helper Methods ==============

  /**
   * Send an event to Google Analytics
   */
  private sendEvent(
    eventName: string,
    eventParams?: Record<string, any>
  ): void {
    if (!this.canTrack()) {
      this.log('warn', `GA not initialized, skipping event: ${eventName}`);
      return;
    }

    try {
      window.gtag('event', eventName, eventParams);
      this.log('info', `GA Event: ${eventName}`, eventParams);
    } catch (error) {
      this.log('error', `Failed to track event ${eventName}:`, error);
    }
  }

  /**
   * Check if we can track events
   */
  private canTrack(): boolean {
    return this.isInitialized && typeof window.gtag === 'function';
  }

  /**
   * Format items array to ensure all items have required fields
   */
  private formatItems(items: GA4Item[]): GA4Item[] {
    return items.map((item, index) => ({
      ...item,
      item_id: String(item.item_id),
      item_name: this.cleanText(item.item_name),
      price: this.ensureNumber(item.price),
      quantity: Math.max(1, Math.round(item.quantity || 1)),
      affiliation: item.affiliation || this.affiliation,
      index: item.index !== undefined ? item.index : index
    }));
  }

  /**
   * Ensure a value is a valid number
   */
  private ensureNumber(value: any): number {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Clean text by removing HTML tags and trimming
   */
  private cleanText(text: string): string {
    if (!text) return '';
    return text.replace(/<[^>]*>/g, '').trim();
  }

  /**
   * Log messages based on debug mode
   */
  private log(
    level: 'info' | 'warn' | 'error',
    message: string,
    ...args: any[]
  ): void {
    if (!this.debugMode && level === 'info') return;

    const prefix = '[GA4]';
    switch (level) {
      case 'info':
        console.log(prefix, message, ...args);
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'error':
        console.error(prefix, message, ...args);
        break;
    }
  }
}
